import { computed, shallowRef, toValue, watch, onScopeDispose } from "vue";
import { createCategoryFilterApi, sectionFor } from "#ce/shared/filters/category/api/category-filter";

function hasScopeValues(scope) {
  return Object.values(scope ?? {}).some((value) => value != null && String(value).length > 0);
}

/** A scoped row's id is its `slug`, else its `value`, else its `id`, as a string. */
function idOf(row) {
  return String(row?.slug ?? row?.value ?? row?.id);
}

// Returns the data source, or null when nothing names an endpoint.
function apiFor({ api, name, url }) {
  if (api) {
    return api;
  }

  return url ? createCategoryFilterApi({ section: sectionFor(name), url }) : null;
}

/**
 * The set of option ids a provider-search scope still matches, for the list filters a client names
 * under `filter_scopes`. Unscoped, every id is in scope. With neither an `api` nor a `url`, the
 * facet has no data source and stays unscoped: `setScope` makes no request.
 *
 * @param {Object} [options]
 * @param {Object} [options.api] - Data source; defaults to `createCategoryFilterApi` over `url`.
 * @param {String} [options.name] - The filter's form field name, which names the endpoint section.
 * @param {String} [options.url] - The endpoint the scoped ids are read from.
 * @param {import("vue").MaybeRefOrGetter<Boolean>} [options.enabled] - Whether the facet fetches
 *   its scoped ids. Disabled, `setScope` makes no request and the scope given is remembered;
 *   once it turns true the ids reflect the latest scope, fetched then.
 */
export function useCategoryFilterScope(options = {}) {
  const api = apiFor(options);
  const enabled = () => toValue(options.enabled ?? true);

  // Null while the facet is unscoped, which admits every id.
  const scopedIds = shallowRef(null);
  const error = shallowRef(null);

  // Counts every scope request so a slow earlier response cannot overwrite the ids of the search
  // the visitor has since moved on to.
  let latestScopeRequest = 0;

  // The scope most recently given, applied when the facet turns enabled.
  let currentScope;

  /**
   * Narrows the ids in scope to the options the given provider-search filters still match. A
   * scope with no values restores the full list.
   *
   * @param {Object} [scope]
   * @returns {Promise<Boolean>} Whether the scoped ids were fetched. A scope with no values needs
   *   no request and reports true, as does a facet without a data source.
   */
  async function setScope(scope) {
    const request = (latestScopeRequest += 1);
    currentScope = scope;

    if (!enabled() || !api) {
      return true;
    }

    if (!hasScopeValues(scope)) {
      scopedIds.value = null;

      return true;
    }

    const isCurrent = () => request === latestScopeRequest;

    try {
      const rows = await api.all({ scope });

      if (isCurrent()) {
        scopedIds.value = new Set(rows.map(idOf));
        error.value = null;
      }

      return true;
    } catch (requestError) {
      if (isCurrent()) {
        error.value = requestError;
      }

      return false;
    }
  }

  const isScoped = computed(() => scopedIds.value != null);

  /**
   * Whether an option id is in scope. Every id is while the facet is unscoped.
   *
   * @param {String} id
   * @returns {Boolean}
   */
  function isInScope(id) {
    return scopedIds.value == null || scopedIds.value.has(String(id));
  }

  // Disabling retires every request in flight, so a response landing later is not current.
  function syncEnabled(isEnabled) {
    if (!isEnabled) {
      latestScopeRequest += 1;
      scopedIds.value = null;

      return;
    }

    if (hasScopeValues(currentScope)) {
      setScope(currentScope);
    }
  }

  function dispose() {
    latestScopeRequest += 1;
  }

  onScopeDispose(dispose);
  watch(enabled, syncEnabled);

  return { scopedIds, isScoped, error, setScope, isInScope };
}
